import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import { TAllSessions, TSession } from 'src/types/session.type';
import { getBundleListAction } from './bundle.store';
import logger from 'src/logger';
import { getAllSessions, saveAllSessions, createCustomerAccountSession } from 'src/helpers/all-session.helper';
import { miniSerializeErrorOverride } from './serialize-error';
import { validateAdminPanelTokenAction } from './token.store';

// Initial state - an array of customer account sessions
const initialState: TAllSessions[] = getAllSessions();

export const allSessionsSlice = createSlice({
  name: 'allSessions',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateAllSessionsAction.fulfilled, (state, action) => {
      if (!action.payload) {
        return [];
      }
      return action.payload;
    });

    builder.addCase(clearAllSessionsAction.fulfilled, () => {
      return [];
    });

    builder.addCase(validateAllCustomerTokensAction.fulfilled, (state, action) => {
      if (!state.length) return state;

      // Map each session to include its validation result
      return state.map((session, index) => {
        const isExpired = action.payload[index] !== undefined ? action.payload[index] : false;
        return { ...session, isTokenExpired: isExpired };
      });
    });
  },
});

/**
 * Updates all sessions based on current session
 */
export const updateAllSessionsAction = createAsyncThunk<TAllSessions[] | null, TSession | null>(
  'allSessions/update',
  async (session: TSession | null, { dispatch }) => {
    if (!session) {
      return null;
    }

    // Only process customer account sessions
    if (session.isCustomerAccount) {
      try {
        // Fetch bundles for this session
        const bundles = await dispatch(getBundleListAction(session)).unwrap();

        // Create the customer account session
        const customerSession = createCustomerAccountSession(session, bundles);

        // Create array of sessions
        const customerSessions = [customerSession];

        // Save to storage as array
        saveAllSessions(customerSessions);

        return customerSessions;
      } catch (error) {
        logger.reportError(error, {
          message: 'Error creating customer account session',
        });
        throw error;
      }
    }

    // For non-customer account sessions, return what's already in storage
    const existingSessions = getAllSessions();
    if (existingSessions.length > 0) {
      return existingSessions; // Return all existing sessions
    }

    return null;
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

/**
 * Validates the tokens of all customer account sessions and updates their expiration status.
 * If the token is valid, it returns `false`; if expired, it returns `true`.
 * 
 * @returns {boolean[]} Array of validation results: `true` for valid tokens, `false` for expired ones.
 */
export const validateAllCustomerTokensAction = createAsyncThunk<boolean[], void>(
  'allSessions/validateTokens',
  async (_, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const sessions = state.allSessions;

      // If there are no sessions, return an empty array
      if (!sessions || sessions.length === 0) {
        return [];
      }

      // Validate each customer account session's token
      const validationResults = await Promise.all(
        sessions.map(async (session) => {
          // Non-customer accounts: we don't need to validate they are webmail long lived tokens
          if (!session?.isCustomerAccount) {
            return false;
          }

          // Customer account without token: expired
          if (!session?.token) {
            return true;
          }

          // Validate the token
          try {
            await dispatch(validateAdminPanelTokenAction(session.token)).unwrap();
            // If validation succeeds, return `false` (token is valid)
            return false;
          } catch (error) {
            // If validation fails, return `true` (token is expired)
            return true;
          }
        })
      );

      // Update session statuses with the token validation results
      const updatedSessions = sessions.map((session, index) => {
        return { ...session, isTokenExpired: validationResults[index] || false };
      });

      // Save updated session statuses to storage
      saveAllSessions(updatedSessions);

      return validationResults;
    } catch (error) {
      logger.reportError(error, {
        message: 'Error validating customer account tokens',
      });
      // In case of an error, return an empty array
      return [];
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

/**
 * Clear all sessions
 */
export const clearAllSessionsAction = createAsyncThunk<null>('allSessions/clear', async () => {
  // Clear session from storage
  saveAllSessions(null);
  return null;
});

/**
 * Selector for all sessions
 * 
 * Note: This selector returns an array that only contains the most recent customer account session.
 * When a new customer account session is created, it replaces any existing ones.
 */
export const allSessionsSelector = (state: RootState) => state.allSessions;

export default allSessionsSlice.reducer;
